import { io } from 'socket.io-client';

// Use relative URL in production, full URL in development
const WS_URL = process.env.REACT_APP_WS_URL || window.location.origin;

class SocketService {
  constructor() {
    this.socket = null;
    this.connected = false;
  }

  /**
   * Connect to the WebSocket server
   */
  connect() {
    if (this.socket && this.connected) {
      console.log('♻️ Reusing existing socket connection');
      return this.socket;
    }

    console.log('🔌 Creating new socket connection');
    this.socket = io(WS_URL, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    this.socket.on('connect', () => {
      console.log('✅ Connected to WebSocket server');
      this.connected = true;
    });

    this.socket.on('disconnect', () => {
      console.log('❌ Disconnected from WebSocket server');
      this.connected = false;
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Connection error:', error);
    });

    return this.socket;
  }

  /**
   * Disconnect from the WebSocket server
   */
  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected = false;
    }
  }

  /**
   * Join a session
   */
  joinSession(sessionId) {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('join-session', sessionId);
  }

  /**
   * Leave a session
   */
  leaveSession() {
    if (!this.socket) {
      return;
    }
    this.socket.emit('leave-session');
  }

  /**
   * Send text to a session
   */
  sendText(sessionId, content) {
    if (!this.socket) {
      throw new Error('Socket not connected');
    }
    this.socket.emit('send-text', { sessionId, content });
  }

  /**
   * Listen for session joined event
   */
  onSessionJoined(callback) {
    if (!this.socket) return;
    this.socket.on('session-joined', callback);
  }

  /**
   * Listen for session not found event
   */
  onSessionNotFound(callback) {
    if (!this.socket) return;
    this.socket.on('session-not-found', callback);
  }

  /**
   * Listen for user count updates
   */
  onUserCountUpdated(callback) {
    if (!this.socket) return;
    this.socket.on('user-count-updated', callback);
  }

  /**
   * Listen for file uploaded event
   */
  onFileUploaded(callback) {
    if (!this.socket) return;
    this.socket.on('file-uploaded', callback);
  }

  /**
   * Listen for file removed event
   */
  onFileRemoved(callback) {
    if (!this.socket) return;
    this.socket.on('file-removed', callback);
  }

  /**
   * Listen for text updated event
   */
  onTextUpdated(callback) {
    if (!this.socket) return;
    this.socket.on('text-updated', callback);
  }

  /**
   * Listen for error event
   */
  onError(callback) {
    if (!this.socket) return;
    this.socket.on('error', callback);
  }

  /**
   * Remove all listeners
   */
  removeAllListeners() {
    if (!this.socket) return;
    this.socket.removeAllListeners();
  }

  /**
   * Get socket ID
   */
  getSocketId() {
    return this.socket ? this.socket.id : null;
  }
}

// Export singleton instance
const socketService = new SocketService();
export default socketService;
